import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import SysRecord from "app/class/record";
import { LogicalNode } from "app/types/record-management-types";
import { RecordManagement } from "app/class/record-management";
import queryKeys from "shared/services/queryKeys";

import { BridgeRecord, ResolvedRelatedList } from "../types";
import { buildRelatedListQueryParams } from "../services/relatedListQuery";

const EMPTY_RELATION_SENTINEL = "__related_list_no_match__";

const buildManyToManyQueryParams = (targetIds: unknown[]): LogicalNode => ({
    type: "logical",
    operator: "and",
    conditions: [
        {
            type: "condition",
            field: "id",
            operator: "in",
            value: targetIds.length > 0 ? targetIds : [EMPTY_RELATION_SENTINEL],
        },
    ],
});

export const useRelatedListRuntime = (
    relatedList: ResolvedRelatedList,
    routeId: string
) => {
    const queryClient = useQueryClient();
    const isManyToMany = relatedList.relationshipType === "many_to_many";
    const bridge = relatedList.bridge;
    const bridgeQueryKey = queryKeys.records.list(
        bridge?.tableName || "missing_bridge_table",
        "bridge",
        {
            routeId,
            sourceColumnName: bridge?.sourceColumnName,
            targetColumnName: bridge?.targetColumnName,
        }
    );

    const { data: bridgeRecords = [], isLoading } = useQuery({
        queryKey: bridgeQueryKey,
        queryFn: async () => {
            if (!bridge?.tableName || !bridge.sourceColumnName || !bridge.targetColumnName) {
                return [];
            }

            const response = await new RecordManagement<SysRecord>(bridge.tableName)
                .where(bridge.sourceColumnName, routeId)
                .run();

            return response.data
                .map((record) => ({
                    id: String(record.id),
                    targetId: String(record?.[bridge.targetColumnName]),
                    raw: record,
                }))
                .filter((record) => Boolean(record.targetId)) as BridgeRecord[];
        },
        enabled: isManyToMany,
    });

    const bridgeTargetIds = useMemo(
        () => bridgeRecords.map((record) => record.targetId),
        [bridgeRecords]
    );

    const queryParams = useMemo(() => {
        if (isManyToMany) {
            return buildManyToManyQueryParams(bridgeTargetIds);
        }

        return buildRelatedListQueryParams(relatedList, routeId);
    }, [bridgeTargetIds, isManyToMany, relatedList, routeId]);

    const reload = useCallback(async () => {
        await queryClient.invalidateQueries({
            queryKey: bridgeQueryKey,
            exact: false,
        });
    }, [bridgeQueryKey, queryClient]);

    return {
        queryParams,
        bridgeRecords,
        reload,
        isLoading: isManyToMany ? isLoading : false,
    };
};
